import { BaseFeature } from '../../core/base-feature.js';

/**
 * MemoryMatchFeature - Frontend feature for Memory Match game
 *
 * Controls the desktop overlay game from the frontend.
 * Receives full game state including all card positions (complete intel).
 * Can swap cards, sabotage, and auto-play with perfect knowledge.
 */
export class MemoryMatchFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'memory-match',
            name: 'Memory Match',
            version: 'v1.0',
            tier: 'external',
            capabilities: ['start', 'flip', 'swap_cards', 'sabotage', 'auto_play', 'get_state', 'end'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[MemoryMatch] Initializing...');
        console.log('[MemoryMatch] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);
                break;

            case 'start_response':
            case 'flip_response':
            case 'swap_cards_response':
            case 'sabotage_response':
            case 'auto_play_response':
            case 'get_state_response':
            case 'end_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[MemoryMatch] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    // ==================== Game Actions ====================

    startGame(config = {}) {
        return this._sendRequest('start', config);
    }

    flipCard(position) {
        return this._sendRequest('flip', { position });
    }

    swapCards(pos1, pos2) {
        return this._sendRequest('swap_cards', { pos1, pos2 });
    }

    sabotage(action) {
        return this._sendRequest('sabotage', { action });
    }

    autoPlay() {
        return this._sendRequest('auto_play');
    }

    getState() {
        return this._sendRequest('get_state');
    }

    endGame() {
        return this._sendRequest('end');
    }

    // ==================== Event Subscriptions ====================

    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[MemoryMatch] Error in state callback:', error);
            }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        console.log('[MemoryMatch] Cleaning up...');
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
